import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';

import { AppAuthService, UserType } from '@src/app/core/services/app-auth.service';
import { SIGN_IN_STEP } from '@src/app/mepco-public/models/mfa-challenges.model';
import { AuthError } from 'aws-amplify/auth';
import { NgOtpInputConfig } from 'ng-otp-input';
import { map } from 'rxjs/operators';

import { MOBILE_BREAKPOINT } from '../mfa.constants';

@Component({
  selector: 'app-mfa-challenge',
  templateUrl: './mfa-challenge.component.html',
  styleUrls: ['./mfa-challenge.component.scss'],
  standalone: false,
})
export class MfaChallengeComponent {
  private static readonly SESSION_TIMEOUT_MS = 3 * 60 * 1000;
  private static readonly RESEND_HINT_DELAY_MS = 2 * 60 * 1000;
  showResendHint = signal<boolean>(false);
  private destroyRef = inject(DestroyRef);

  private _authService = inject(AppAuthService);
  private readonly renderedAt = Date.now();
  tooManyAttempts = signal<boolean>(false);
  limitExceededException = signal<boolean>(false);

  softwareTokenMFA = SIGN_IN_STEP.SOFTWARE_TOKEN_MFA;

  selectedMethod = input.required<SIGN_IN_STEP>();
  mustVerify = input<boolean>(false);
  confirmSigned = output<boolean>();

  isLoading = false;
  errorMessage: string | null = null;

  verificationCode = new FormControl<string>('', {
    validators: [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
  });

  private _breakpointObserver = inject(BreakpointObserver);

  private _isMobile = toSignal(this._breakpointObserver.observe(MOBILE_BREAKPOINT).pipe(map(state => state.matches)), {
    initialValue: this._breakpointObserver.isMatched(MOBILE_BREAKPOINT),
  });

  private _desktopOtpConfig: NgOtpInputConfig = {
    length: 6,
    allowNumbersOnly: true,
    showError: false,
    inputStyles: {
      width: '36px',
      height: '40px',
      'font-size': '18px',
      margin: '0 4px',
      'border-radius': '6px',
      'text-align': 'center',
    },
    containerStyles: {
      display: 'inline-block',
      padding: '24px',
      border: '1px solid var(--primary-500)',
      'border-radius': '8px',
    },
  };

  private _mobileOtpConfig: NgOtpInputConfig = {
    ...this._desktopOtpConfig,
    inputStyles: {
      ...this._desktopOtpConfig.inputStyles,
      width: '28px',
      height: '34px',
      'font-size': '15px',
      margin: '0 2px',
    },
    containerStyles: {
      ...this._desktopOtpConfig.containerStyles,
      padding: '12px',
    },
  };

  otpConfig = computed(() => (this._isMobile() ? this._mobileOtpConfig : this._desktopOtpConfig));

  isCorporateUser = signal<boolean>(this._authService.userType() === UserType.Corporate);
  isSessionExpired = signal<boolean>(false);

  constructor() {
    effect(async () => {
      if (this.mustVerify()) await this.generateCode();
    });

    const timeoutId = setTimeout(() => this.showResendHint.set(true), MfaChallengeComponent.RESEND_HINT_DELAY_MS);
    this.destroyRef.onDestroy(() => clearTimeout(timeoutId));

    this.verificationCode.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.errorMessage = null;
    });
  }

  async generateCode() {
    try {
      switch (this.selectedMethod()) {
        case SIGN_IN_STEP.SMS_MFA:
          await this._authService.sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
          break;

        case SIGN_IN_STEP.EMAIL_MFA:
          await this._authService.sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
          break;
      }
    } catch (error) {
      if (error instanceof AuthError && error.name === 'LimitExceededException') {
        this.limitExceededException.set(true);
        this.isSessionExpired.set(false);
      } else {
        this.errorMessage = error?.message ?? 'Verification failed. Please try again.';
      }
    }
  }

  onSubmit() {
    if (this.verificationCode.invalid || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    // Verify the new MFA setup or confirm the sign-in with the MFA challenge
    if (this.mustVerify()) {
      this.onVerify();
    } else {
      this.onConfirm();
    }
  }

  async onVerify(): Promise<void> {
    if (Date.now() - this.renderedAt >= MfaChallengeComponent.SESSION_TIMEOUT_MS) {
      this.isSessionExpired.set(true);
      this.tooManyAttempts.set(false);
      this.isLoading = false;
      return;
    }

    try {
      switch (this.selectedMethod()) {
        case SIGN_IN_STEP.SOFTWARE_TOKEN_MFA:
          await this._authService.verifyAndSetPreferredTOTP(this.verificationCode.value);

          break;
        case SIGN_IN_STEP.SMS_MFA:
          await this._authService.verifyAndSetPreferredSMS(this.verificationCode.value);

          break;
        case SIGN_IN_STEP.EMAIL_MFA:
          await this._authService.verifyAndSetPreferredEmail(this.verificationCode.value);

          break;
        default:
          throw new Error('Unsupported MFA method selected.');
      }
      this.confirmSigned.emit(true);
    } catch (error: any) {
      if (error instanceof AuthError && error.name === 'LimitExceededException') {
        this.limitExceededException.set(true);
        this.isSessionExpired.set(false);
      } else {
        this.errorMessage = 'Invalid code';
      }
    } finally {
      this.isLoading = false;
    }
  }

  async onConfirm(): Promise<void> {
    try {
      const code = this.verificationCode.value.trim();

      const { isSignedIn } = await this._authService.confirmSignIn(code, false);

      this.confirmSigned.emit(isSignedIn);
    } catch (error: any) {
      if (error instanceof AuthError && error.name === 'NotAuthorizedException') {
        if (error.message.includes('Invalid session')) {
          this.isSessionExpired.set(true);
          this.tooManyAttempts.set(false);
        }
        if (error.message.includes('Too many')) {
          this.tooManyAttempts.set(true);
          this.isSessionExpired.set(false);
        }
      } else {
        this.errorMessage = 'Invalid code';
      }
    } finally {
      this.isLoading = false;
    }
  }

  closeDialog() {
    this._authService.logout();
    this.confirmSigned.emit(false);
  }
}
